import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { DateRangePicker, Icon, Styles } from "@/shared/coralreef";
import { percent, rem } from "csx";
import { Content, Horizontal, Stretch } from "gls/lib";
import moment from "moment";
import React, { FC, useEffect, useRef, useState } from "react";
import type { Cage, Site } from "@/shared/context/sites.graphql";
import { style, stylesheet } from "typestyle";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import { SiteCagePicker } from "./siteCagePicker/SiteCagePicker";
import { LocalServerPicker } from "./LocalServerPicker";

export type MappedToolbarSite = {
  activeSites: MappedSite[];
  fallowedSites: FallowedSiteWithYear[];
};

export type FallowedSiteWithYear = MappedSite & { year: string };

export type MappedSite = Omit<Site, "cageGroups"> & {
  cageGroups: MappedCageGroup[];
};

export type MappedCageGroup = {
  id: number;
  name: string;
  cages: Cage[];
};

export const Toolbar: FCWithChildren<{
  icon: IconDefinition;
  title: string;
  withDates?: boolean;
  hideSitePicker?: boolean;
  isSingleSite?: boolean;
  localServerPicker?: boolean;
}> = React.memo((props) => {
  const {
    children,
    icon,
    title,
    hideSitePicker,
    withDates,
    localServerPicker,
  } = props;
  const host = useShellHost();
  const [range, setRangeState] = useState(host.handle.dateRange.range.get());
  useEffect(
    () =>
      host.handle.dateRange.range.subscribe(() =>
        setRangeState(host.handle.dateRange.range.get()),
      ),
    [host],
  );
  const start = range.start ? moment(range.start) : null;
  const end = range.end ? moment(range.end) : null;

  // Slot refs: shell publishes these DOM nodes so MFEs can portal into them.
  // Claim flags are set by the MFE side; while claimed, the shell hides its
  // own fallback rendering so only the MFE's portaled content shows.
  const leftRef = useRef<HTMLDivElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);
  const [leftClaimed, setLeftClaimed] = useState<boolean>(() =>
    host.handle.topbarSlots.leftClaimed.get(),
  );
  const [centerClaimed, setCenterClaimed] = useState<boolean>(() =>
    host.handle.topbarSlots.centerClaimed.get(),
  );
  useEffect(
    () =>
      host.handle.topbarSlots.leftClaimed.subscribe(() =>
        setLeftClaimed(host.handle.topbarSlots.leftClaimed.get()),
      ),
    [host],
  );
  useEffect(
    () =>
      host.handle.topbarSlots.centerClaimed.subscribe(() =>
        setCenterClaimed(host.handle.topbarSlots.centerClaimed.get()),
      ),
    [host],
  );
  useEffect(() => {
    return () => {
      host.handle.topbarSlots.setLeft(null);
      host.handle.topbarSlots.setCenter(null);
    };
  }, [host]);

  return (
    <Horizontal
      height={TOOLBAR_HEIGHT}
      className={styles.toolbar}
      verticalAlign="center"
      padding={[0, rem(2)]}
      spacing={rem(2)}
      data-testid="toolbar"
      width={percent(100)}
    >
      <div
        ref={(el) => {
          leftRef.current = el;
          host.handle.topbarSlots.setLeft(el);
        }}
        data-testid="shell-topbar-slot-left"
      >
        {!leftClaimed && <ToolbarBreadCrumb icon={icon} title={title} />}
      </div>

      <Stretch horizontalAlign="center">
        <div
          ref={(el) => {
            centerRef.current = el;
            host.handle.topbarSlots.setCenter(el);
          }}
          data-testid="shell-topbar-slot-center"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          {!centerClaimed && children}
        </div>
      </Stretch>
      <Horizontal verticalAlign="center">
        {withDates && (
          <DateRangePicker
            withConfirmButton
            endDate={end}
            startDate={start}
            maxDate={moment().endOf("D")}
            onChange={({ startDate, endDate }) => {
              host.handle.dateRange.setRange(
                startDate
                  ? startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss")
                  : null,
                endDate
                  ? endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss")
                  : null,
              );
            }}
          />
        )}
        {localServerPicker ? (
          <LocalServerPicker />
        ) : (
          !hideSitePicker && <SiteCagePicker />
        )}
      </Horizontal>
    </Horizontal>
  );
});

const ToolbarBreadCrumb: FC<{
  icon: IconDefinition;
  title: string;
}> = (props) => {
  const { icon, title } = props;
  return title ? (
    <Horizontal
      height={percent(100)}
      spacing={rem(0.5)}
      verticalAlign="center"
      className={style({ color: Styles.Colours.textLight })}
      data-testid="toolbar-breadcrumb"
    >
      <Icon icon={icon} />
      <Content
        verticalAlign="bottom"
        height={rem(1.6)}
        data-testid="toolbar-breadcrumb-text"
      >
        {title}
      </Content>
    </Horizontal>
  ) : null;
};

export const TOOLBAR_HEIGHT = rem(3);

const styles = stylesheet({
  toolbar: {
    background: Styles.Colours.darkerBlue,
    boxShadow: `0 4px 6px rgb(0 0 0 / 15%)`,
    zIndex: 998,
    position: "relative",
    boxSizing: "border-box",
  },
});
